import { computed, inject, Injectable, signal } from '@angular/core';
import { debounceTime, Observable, of, switchMap, take } from 'rxjs';

import { ENVIRONMENT } from '@environment';
import { UserInteractionService } from '@usheru-hff/community-elements/utils';
import { LocationService } from '@usheru-hff/sdk/location';
import { MovieService } from '@usheru-hff/sdk/movie';
import { Movie, MovieListWrapper, SearchItemType } from '@usheru-hff/shared/domain';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';

import { SearchMovieFilterInterface } from './entities/search-movie-filter.interface';
import { SearchMovieStateInterface } from './entities/search-movie-state.interface';
import { movieFilter } from './search-filter-options';

@Injectable()
export class SearchMovieService {
  private readonly movieService = inject(MovieService);
  private readonly userService = inject(UserInteractionService);
  private readonly translationsService = inject(TranslationsService);
  private readonly locationService = inject(LocationService);
  private readonly env = inject(ENVIRONMENT);

  private readonly initialState: SearchMovieStateInterface = {
    isLoading: false,
    filter: movieFilter,
    results: null,
    keyword: '',
    isRedirectEnabled: true,
    selectedItem: null
  };
  private state = signal<SearchMovieStateInterface>(this.initialState);
  private readonly isCountryBasedApplication = this.env.countryBasedApplication?.enabled;
  filter = computed(() => this.state().filter);
  results = computed(() => this.state().results);
  isLoading = computed(() => this.state().isLoading);
  keyword = computed(() => this.state().keyword);
  isRedirectEnabled = computed(() => this.state().isRedirectEnabled);
  selectedItem = computed(() => this.state().selectedItem);

  searchResults() {
    const type = this.filter().type;
    this.setIsLoading(true);
    switch (type) {
      case 'star':
        this.searchStars();
        break;
      case 'movie':
        this.searchMovies();
        break;
      case 'user':
        this.searchUsers();
        break;
    }
  }

  setResults(results: SearchItemType[]) {
    this.state.update(state => ({
      ...state,
      results
    }));
  }

  setIsLoading(isLoading: boolean) {
    this.state.update(state => ({
      ...state,
      isLoading
    }));
  }

  setFilter(filter: SearchMovieFilterInterface) {
    this.state.update(state => ({
      ...state,
      filter
    }));
    if (this.keyword()) this.searchResults();
  }

  setPlaceholder(placeholder: string) {
    this.state.update(state => ({
      ...state,
      filter: {
        ...state.filter,
        placeholder
      }
    }));
  }

  setKeyword(keyword: string) {
    this.state.update(state => ({
      ...state,
      keyword: keyword
    }));
  }

  setIsRedirectEnabled(isRedirectEnabled: boolean) {
    this.state.update(state => ({
      ...state,
      isRedirectEnabled
    }));
  }

  setSelectedItem(selectedItem: SearchItemType) {
    this.state.update(state => ({
      ...state,
      selectedItem
    }));
  }

  searchMovies(): void {
    this.getMovies()
      .pipe(take(1))
      .subscribe(movies => {
        this.setResults(movies);
        this.setIsLoading(false);
      });
  }
  searchStars() {
    this.movieService
      .getStarList({ keyword: this.keyword() })
      .pipe(take(1))
      .subscribe(stars => {
        this.setResults(stars);
        this.setIsLoading(false);
      });
  }
  searchUsers() {
    this.userService
      .searchUser(this.keyword())
      .pipe(take(1))
      .subscribe(users => {
        this.setResults(users);
        this.setIsLoading(false);
      });
  }

  private getMovies(): Observable<Movie[]> {
    const filtersWrapper: MovieListWrapper = {
      keyword: this.keyword(),
      pageOffset: 25,
      trLanguage: this.translationsService.currentLanguage().code,
      ...(this.isCountryBasedApplication ? { locCountry: this.locationService.selectedCountry.slug } : {})
    };
    // adding same custom tags filters of movies page to the search component used in header
    if (this.env.moviesPageCustomTags) {
      filtersWrapper.tags = this.env.moviesPageCustomTags;
    }
    // adding sort by position order to get movies query, this will affect only if position is added manually to some principal, movies
    filtersWrapper.sort = 'positionOrder';
    return this.movieService.getMoviesCompact(filtersWrapper);
  }

  searchMoviesOrStars(): void {
    this.setIsLoading(true);
    this.getMovies()
      .pipe(
        take(1),
        switchMap(movies => {
          if (!movies || movies.length === 0) return this.movieService.getStarList({ keyword: this.keyword() });
          return of(movies);
        })
      )
      .subscribe(results => {
        // time out is for ngZone, specially for when the api call is cached
        setTimeout(() => {
          this.setResults(results);
          this.setIsLoading(false);
        }, 10);
      });
  }

  resetState(filter?: SearchMovieFilterInterface) {
    filter = filter || this.initialState.filter;
    this.state.update(s => ({
      ...this.initialState,
      results: s.results,
      filter,
      isRedirectEnabled: this.isRedirectEnabled(),
      selectedItem: this.selectedItem()
    }));
  }
}
